import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { motion } from 'framer-motion';

type Variant = 'primary' | 'secondary' | 'gold' | 'icon' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const MotionButton = motion.create('button');

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading, disabled, children, className = '', ...props }, ref) => {
    const isDisabled = disabled || loading;

    const base =
      'inline-flex items-center justify-center gap-2 rounded font-body font-semibold text-sm transition-colors cursor-pointer';

    const variants: Record<Variant, string> = {
      primary:
        'bg-indigo-500 text-white shadow-glow-indigo',
      secondary:
        'bg-transparent border-[1.5px] border-indigo-500 text-indigo-300',
      gold:
        'bg-gold-500 text-dark-bg shadow-glow-gold',
      icon:
        'bg-transparent text-content-muted rounded-full',
      ghost:
        'bg-transparent text-content-muted',
    };

    const sizes: Record<Size, string> = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-6 py-3 text-base',
    };

    const iconSizes: Record<Size, string> = {
      sm: 'p-1.5',
      md: 'p-2',
      lg: 'p-3',
    };

    const hoverColors: Record<Variant, string> = {
      primary: 'hover:bg-[#7E70EF]',
      secondary: 'hover:bg-indigo-500/10 hover:border-indigo-300 hover:text-indigo-200',
      gold: 'hover:bg-gold-400',
      icon: 'hover:bg-dark-surface hover:text-content',
      ghost: 'hover:bg-dark-surface hover:text-content',
    };

    return (
      <MotionButton
        ref={ref}
        disabled={isDisabled}
        className={`
          ${base}
          ${variants[variant]}
          ${variant === 'icon' ? iconSizes[size] : sizes[size]}
          ${isDisabled ? 'opacity-40 cursor-not-allowed' : hoverColors[variant]}
          ${className}
        `}
        whileHover={isDisabled ? undefined : { scale: 1.02, y: -1 }}
        whileTap={isDisabled ? undefined : { scale: 0.98 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
        {...(props as any)}
      >
        {loading ? (
          <>
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            <span className="animate-shimmer bg-clip-text">Loading…</span>
          </>
        ) : (
          children
        )}
      </MotionButton>
    );
  },
);

Button.displayName = 'Button';
